import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Users, DollarSign, TrendingUp, Calendar, Crown,
  RefreshCw, Search, ChevronDown, ChevronUp, Mail,
  Clock, CheckCircle, AlertCircle, Filter
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSubscriptions({ token, isDarkMode }) {
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all, premium, trial, free
  const [expandedUser, setExpandedUser] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (token) {
      fetchAdminData();
    }
  }, [token]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const [statsRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/admin/subscription-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/subscribers`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(statsRes.data);
      setSubscribers(subscribersRes.data.subscribers || []);
      setRecentPurchases(subscribersRes.data.recent_purchases || []);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (subscription) => {
    if (!subscription || !subscription.plan) {
      return { label: "Free", color: "bg-gray-500/20 text-gray-400" };
    }
    if (subscription.plan === "premium" && subscription.status === "active") {
      return { label: "Premium", color: "bg-green-500/20 text-green-400" };
    }
    if (subscription.plan === "trial") {
      return { label: "Trial", color: "bg-yellow-500/20 text-yellow-400" };
    }
    if (subscription.status === "expired") {
      return { label: "Expired", color: "bg-red-500/20 text-red-400" };
    }
    return { label: subscription.plan, color: "bg-gray-500/20 text-gray-400" };
  };

  const filteredSubscribers = subscribers.filter(sub => {
    const matchesSearch = 
      sub.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (filter === "all") return true;
    if (filter === "premium") return sub.subscription?.plan === "premium" && sub.subscription?.status === "active";
    if (filter === "trial") return sub.subscription?.plan === "trial";
    if (filter === "free") return !sub.subscription?.plan || sub.subscription?.plan === "free";
    
    return true;
  });

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 bg-white/10 rounded-xl"></div>
          ))}
        </div>
        <div className="h-64 bg-white/10 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
            <Crown className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Admin Dashboard</h2>
            <p className="text-sm opacity-60">Subscription & Purchase Management</p>
          </div>
        </div>
        <Button
          onClick={fetchAdminData}
          variant="ghost"
          className="text-white/60"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-400" />
            <span className="text-sm opacity-60">Total Users</span>
          </div>
          <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
        </div>
        
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Crown className="w-5 h-5 text-yellow-400" />
            <span className="text-sm opacity-60">Premium</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{stats?.premium_users || 0}</p>
        </div>
        
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-orange-400" />
            <span className="text-sm opacity-60">Trial</span>
          </div>
          <p className="text-2xl font-bold text-yellow-400">{stats?.trial_users || 0}</p>
        </div>
        
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            <span className="text-sm opacity-60">Conversion</span>
          </div>
          <p className="text-2xl font-bold text-purple-400">
            {stats?.conversion_rate?.toFixed(1) || 0}%
          </p>
        </div>
      </div>

      {/* Recent Purchases */}
      {recentPurchases.length > 0 && (
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-green-500/10' : 'bg-green-50'}`}>
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-green-400">
            <TrendingUp className="w-5 h-5" />
            Recent Purchases
          </h3>
          <div className="space-y-2">
            {recentPurchases.slice(0, 5).map((purchase, idx) => (
              <motion.div
                key={purchase.id || idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  isDarkMode ? 'bg-black/20' : 'bg-white/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#00f2ea] to-[#7000ff] flex items-center justify-center text-sm font-bold">
                    {purchase.user_name?.[0] || "?"}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{purchase.user_name || "Unknown"}</p>
                    <p className="text-xs opacity-50">{purchase.user_email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-400">${purchase.amount?.toFixed(2) || "14.99"}</p>
                  <p className="text-xs opacity-50">{formatDate(purchase.date)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Subscribers List */}
      <div className={`rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
        {/* Search and Filter */}
        <div className="p-4 border-b border-white/10">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 ${isDarkMode ? 'bg-black/30' : 'bg-white'}`}
              />
            </div>
            <div className="flex gap-2">
              {["all", "premium", "trial", "free"].map(f => (
                <Button
                  key={f}
                  size="sm"
                  variant={filter === f ? "default" : "ghost"}
                  onClick={() => setFilter(f)}
                  className={filter === f ? "bg-[#00f2ea] text-black" : ""}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* List */}
        <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
          {filteredSubscribers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm opacity-60">No subscribers found</p>
            </div>
          ) : (
            filteredSubscribers.map((subscriber, idx) => {
              const status = getStatusBadge(subscriber.subscription);
              const isExpanded = expandedUser === subscriber.id;
              
              return (
                <motion.div
                  key={subscriber.id || idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
                >
                  <button
                    onClick={() => setExpandedUser(isExpanded ? null : subscriber.id)}
                    className="w-full p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#00f2ea] to-[#7000ff] flex items-center justify-center font-bold">
                        {subscriber.name?.[0] || subscriber.email?.[0] || "?"}
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{subscriber.name || "No Name"}</p>
                        <p className="text-xs opacity-50 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {subscriber.email}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 opacity-50" />
                      ) : (
                        <ChevronDown className="w-4 h-4 opacity-50" />
                      )}
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      className="px-4 pb-4 border-t border-white/10"
                    >
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 text-sm">
                        <div>
                          <p className="opacity-50 text-xs">Joined</p>
                          <p className="font-medium">{formatDate(subscriber.created_at)}</p>
                        </div>
                        <div>
                          <p className="opacity-50 text-xs">Subscription</p>
                          <p className="font-medium">{subscriber.subscription?.plan || "Free"}</p>
                        </div>
                        <div>
                          <p className="opacity-50 text-xs">Subscribed On</p>
                          <p className="font-medium">
                            {formatDate(subscriber.subscription?.subscribed_at) || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="opacity-50 text-xs">Expires</p>
                          <p className="font-medium">
                            {subscriber.subscription?.is_lifetime 
                              ? "Lifetime" 
                              : formatDate(subscriber.subscription?.subscription_ends) || "N/A"
                            }
                          </p>
                        </div>
                      </div>
                      
                      {subscriber.subscription?.paypal_subscription_id && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-xs opacity-50">
                            PayPal ID: {subscriber.subscription.paypal_subscription_id}
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 text-sm opacity-60">
          Showing {filteredSubscribers.length} of {subscribers.length} subscribers
        </div>
      </div>
    </div>
  );
}
